import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

import {
  Avatar,
  DueDateChip,
  IssueKeyBadge,
  LabelChip,
  PointsBadge,
  PriorityIcon,
  StatusPill,
  TypeIcon,
  cn,
  issueLabel,
  type Issue,
} from '@/features/issue'

/**
 * One backlog row.
 *
 * The drag handle is a separate grip rather than the whole row, because the row
 * itself carries two other gestures — click to open, ⌘/Shift-click to build a
 * selection — and a row that starts dragging on any pointer-down makes both of
 * those feel broken.
 */
export function BacklogRow({
  issue,
  selected,
  onOpen,
  onSelect,
  dragDisabled,
}: {
  issue: Issue
  selected: boolean
  onOpen: (ref: string) => void
  onSelect: (id: string, modifiers: { shift: boolean; meta: boolean }) => void
  dragDisabled?: boolean
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: issue.id,
    disabled: dragDisabled,
    data: { type: 'issue', issue },
  })

  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      className={cn(
        'group flex items-center gap-2 border-b border-ink-200 px-2 py-1.5 last:border-b-0 dark:border-ink-800',
        selected
          ? 'bg-brand-50 dark:bg-brand-950/60'
          : 'bg-white hover:bg-ink-50 dark:bg-ink-950 dark:hover:bg-ink-900',
        isDragging && 'opacity-40',
      )}
    >
      <input
        type="checkbox"
        checked={selected}
        aria-label={`Select ${issueLabel(issue)}`}
        onChange={() => undefined}
        onClick={(event) => {
          event.stopPropagation()
          onSelect(issue.id, { shift: event.shiftKey, meta: event.metaKey || event.ctrlKey })
        }}
        className="size-3.5 shrink-0 rounded border-ink-300 accent-brand-600"
      />

      <button
        type="button"
        {...attributes}
        {...listeners}
        aria-label={`Reorder ${issueLabel(issue)}`}
        disabled={dragDisabled}
        className={cn(
          'shrink-0 cursor-grab touch-none rounded p-0.5 text-ink-300 opacity-0 transition active:cursor-grabbing group-hover:opacity-100',
          'hover:bg-ink-100 hover:text-ink-600 dark:hover:bg-ink-800',
          dragDisabled && 'hidden',
        )}
      >
        <svg viewBox="0 0 16 16" className="size-3.5" fill="currentColor" aria-hidden="true">
          <circle cx="6" cy="4" r="1.2" />
          <circle cx="10" cy="4" r="1.2" />
          <circle cx="6" cy="8" r="1.2" />
          <circle cx="10" cy="8" r="1.2" />
          <circle cx="6" cy="12" r="1.2" />
          <circle cx="10" cy="12" r="1.2" />
        </svg>
      </button>

      <TypeIcon type={issue.issue_type} />
      <IssueKeyBadge issueKey={issueLabel(issue)} done={issue.status === 'done'} className="w-16 shrink-0" />

      <button
        type="button"
        onClick={(event) => {
          if (event.shiftKey || event.metaKey || event.ctrlKey) {
            onSelect(issue.id, { shift: event.shiftKey, meta: event.metaKey || event.ctrlKey })
            return
          }
          onOpen(issue.issue_key ?? issue.id)
        }}
        className="min-w-0 flex-1 truncate text-left text-sm text-ink-800 hover:underline dark:text-ink-100"
      >
        {issue.title}
      </button>

      {issue.labels && issue.labels.length > 0 && (
        <span className="hidden shrink-0 gap-1 lg:flex">
          {issue.labels.slice(0, 2).map((label) => (
            <LabelChip key={label.id} label={label} />
          ))}
        </span>
      )}

      <DueDateChip dueDate={issue.due_date} done={issue.status === 'done'} />
      <PriorityIcon priority={issue.priority} />
      <span className="hidden sm:inline">
        <StatusPill status={issue.status} />
      </span>
      <PointsBadge points={issue.story_points} />
      <Avatar user={issue.assignee ?? null} size="xs" />
    </li>
  )
}
